import { useState } from 'react'
import AuthenticatedLayout from '../../components/AuthenticatedLayout'
import { useForm, Link } from '@inertiajs/react'
import FormField from '../../components/FormField'
import Button from '../../components/Button'
import { uploadFile } from '../../utils/directUpload'

export default function ProductsEdit({ activity, product }) {
  const [orderedImages, setOrderedImages] = useState(product.images || [])

  const { data, setData, patch, processing, errors } = useForm({
    name: product.name,
    description: product.description || '',
    price: product.price,
    images: [],
    remove_image_ids: [],
    image_order: (product.images || []).map(img => img.id),
  })
  const [uploading, setUploading] = useState(false)
  const [fileNames, setFileNames] = useState([])
  const [uploadError, setUploadError] = useState(null)

  const visibleImages = orderedImages.filter(img => !data.remove_image_ids.includes(img.id))

  function markForRemoval(id) {
    setOrderedImages(prev => prev.filter(img => img.id !== id))
    setData({
      ...data,
      remove_image_ids: [...data.remove_image_ids, id],
      image_order: data.image_order.filter(oid => oid !== id),
    })
  }

  function moveImage(index, direction) {
    const newImages = [...visibleImages]
    const targetIndex = index + direction
    if (targetIndex < 0 || targetIndex >= newImages.length) return
    ;[newImages[index], newImages[targetIndex]] = [newImages[targetIndex], newImages[index]]
    setOrderedImages(newImages)
    setData('image_order', newImages.map(img => img.id))
  }

  async function handleFileChange(e) {
    const files = Array.from(e.target.files)
    if (files.length === 0) return

    setFileNames(files.map(f => f.name))
    setUploading(true)
    setUploadError(null)

    try {
      const blobs = await Promise.all(files.map(uploadFile))
      setData('images', blobs.map(b => b.signed_id))
    } catch {
      setUploadError('Uppladdning misslyckades. Försök igen.')
      setData('images', [])
      setFileNames([])
    } finally {
      setUploading(false)
    }
  }

  function handleSubmit(e) {
    e.preventDefault()
    patch(`/activities/${activity.id}/products/${product.id}`)
  }

  return (
    <AuthenticatedLayout>
      <div className="max-w-lg">
        <div className="mb-6">
          <Link href={`/activities/${activity.id}`} className="text-sm text-indigo-600 hover:underline">
            ← {activity.name}
          </Link>
          <h1 className="text-2xl font-bold text-gray-900 mt-2">Redigera produkt</h1>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            <FormField label="Namn" error={errors.name}>
              <input
                type="text"
                value={data.name}
                onChange={e => setData('name', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </FormField>

            <FormField label="Pris (SEK)" error={errors.price}>
              <input
                type="number"
                min="0"
                step="0.01"
                value={data.price}
                onChange={e => setData('price', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </FormField>

            <FormField label="Beskrivning" error={errors.description}>
              <textarea
                value={data.description}
                onChange={e => setData('description', e.target.value)}
                rows={3}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </FormField>

            {visibleImages.length > 0 && (
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Befintliga bilder</p>
                <div className="flex flex-wrap gap-2">
                  {visibleImages.map((img, index) => (
                    <div key={img.id} className="relative">
                      <img
                        src={img.url}
                        alt=""
                        className="w-20 h-20 object-cover rounded-md border border-gray-200"
                      />
                      {index === 0 && (
                        <span className="absolute bottom-0 left-0 right-0 bg-indigo-600 text-white text-[10px] font-semibold text-center py-0.5 rounded-b-md">
                          Huvud
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => markForRemoval(img.id)}
                        className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center hover:bg-red-600"
                      >
                        ×
                      </button>
                      <div className="absolute bottom-0 left-0 right-0 flex justify-between px-0.5 pb-0.5">
                        <button
                          type="button"
                          onClick={() => moveImage(index, -1)}
                          disabled={index === 0}
                          className="w-5 h-5 bg-black/40 text-white rounded text-xs flex items-center justify-center hover:bg-black/60 disabled:opacity-0"
                        >
                          ‹
                        </button>
                        <button
                          type="button"
                          onClick={() => moveImage(index, 1)}
                          disabled={index === visibleImages.length - 1}
                          className="w-5 h-5 bg-black/40 text-white rounded text-xs flex items-center justify-center hover:bg-black/60 disabled:opacity-0"
                        >
                          ›
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <FormField label="Lägg till bilder">
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleFileChange}
                className="w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
              />
              {uploading && (
                <p className="mt-1 text-sm text-gray-500">Laddar upp {fileNames.length} bild{fileNames.length !== 1 ? 'er' : ''}…</p>
              )}
              {!uploading && fileNames.length > 0 && !uploadError && (
                <p className="mt-1 text-sm text-green-600">{fileNames.length} bild{fileNames.length !== 1 ? 'er' : ''} klar{fileNames.length !== 1 ? 'a' : ''} att skicka</p>
              )}
              {uploadError && (
                <p className="mt-1 text-sm text-red-600">{uploadError}</p>
              )}
            </FormField>

            <div className="flex gap-3">
              <Button type="submit" disabled={processing || uploading}>
                {processing ? 'Sparar…' : 'Spara ändringar'}
              </Button>
              <Link href={`/activities/${activity.id}`}>
                <Button type="button" variant="secondary">Avbryt</Button>
              </Link>
            </div>
          </form>
        </div>
      </div>
    </AuthenticatedLayout>
  )
}
